import { CourseRequirementType, ICourse, ICreateCourseRequirement } from '@/types/courses';
import { defaultNewRequirement, defaultSettings, getRequirementMapBySession } from '../utils/course-utils';
import CourseRequirementRow from './course-requirement-row';
import { BrandliveEvent } from '@/types/projects.types';
import { SessionTypesEnum } from '@/types/session.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Popover from '@ui/popover/popover';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Text from '@ui/text/text/text';
import { useMemo, useRef, useState } from 'react';
import Button from '@ui/buttons/button/button';
import NumericStepper from '@ui/numeric-stepper/numeric-stepper';
import { numClamp } from '@/utils/num-clamp';
import Container from '@ui/container/container';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import CoursesCategoriesSelect from './controls/courses-categories-select';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';

type Props = {
  handleSave: (name?: string, description?: string, categories?: string[], credits?: number) => void;
  course: ICourse | null;
  workingEvent: BrandliveEvent | null;
  setRequirementToDelete: (uuid: string) => void;
  setRequirementToUpdate: (uuid: string) => void;
  setNewRequirement: (requirement: ICreateCourseRequirement) => void;
  setShowAddNewRequirementModal: (show: boolean) => void;
  showAddRequirementPopover: boolean;
  setShowAddRequirementPopover: (show: boolean) => void;
};

const canAddMoreRequirements = (
  requirements: ICreateCourseRequirement[] | undefined,
  workingEvent: BrandliveEvent | undefined | null
) => {
  if (!workingEvent) {
    return false;
  }

  if (!requirements) {
    return true;
  }

  // create map to help prevent nested loops
  const requirementsMap = getRequirementMapBySession(requirements);

  // if every session has two requirements already, we can't add more
  for (const session of workingEvent.sessions) {
    // only broadcast sessions can have requirements as of right now
    if (session.session_type === SessionTypesEnum.broadcast) {
      // if the requirement map does not have two requirements on this session then the user
      // can add more requirements
      if (!requirementsMap[session.session]?.length || requirementsMap[session.session].length < 2) {
        return true;
      }
    }
  }

  return false;
};

export default function CourseMainPanel({
  handleSave,
  course,
  workingEvent,
  setRequirementToDelete,
  setRequirementToUpdate,
  setNewRequirement,
  setShowAddNewRequirementModal,
  showAddRequirementPopover,
  setShowAddRequirementPopover,
}: Props) {
  const [courseDescription, setCourseDescription] = useState(course?.description);
  const popoverRef = useRef<HTMLDivElement>(null);
  const openAddRequirementPopover = () => {
    // this button element may be at the very bottom of the viewport,
    // so when the popover opens we need to scroll the page to ensure that
    // the popover is in view
    setShowAddRequirementPopover(!showAddRequirementPopover);
    setTimeout(() => {
      // do in timeout to ensure that the react state has finished updating and rendering
      popoverRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  const defaultCategories = useMemo(() => {
    if (!course?.categories) return [];

    return course?.categories
      .map((c) => {
        return {
          label: `${c}`.trim(),
          value: c,
          meta: c,
        };
      })
      .filter(Boolean) as TReactSelectOption<string, string, string>[];
  }, [course?.categories]);

  const handleAddCategories = (selectedCategories: TReactSelectOption[]) => {
    const updatedCategories = selectedCategories.map((c) => c.value.toString());
    handleSave('', '', updatedCategories);
  };

  return (
    <div className="flex-column gap-12">
      <div className="flex jc-space-between margin-b-30">
        <div className="min-width-540 flex-column gap-10 flex-1" style={{ maxWidth: '400px' }}>
          <Text className="L3" color="var(--gray-200)">
            ABOUT THIS COURSE
          </Text>
          <InputWithOverlay
            border="solid 1px transparent"
            hoverBackground="var(--gray-900)"
            hoverBorder="solid 1px var(--gray-300)"
            background="var(--gray-900)"
            inputHeight="32px"
            inputProps={{
              value: courseDescription || '',
              placeholder: 'Add a description to give your audience more context..',
              className: 'radius-4 padding-10 S3',
              onChange: (e) => setCourseDescription(e.target.value),
              onBlur: (e) => handleSave('', e.target.value),
            }}
          />
        </div>
        <div className="flex-column gap-10">
          <Text className="L3" color="var(--gray-200)">
            CREDITS
          </Text>
          <NumericStepper
            initialValue={course?.credits}
            minValue={0}
            maxValue={10000}
            step={1}
            onChange={(e) => {
              const credits = numClamp(e, 0, 10000);
              handleSave('', '', [], credits);
            }}
          />
        </div>
        <div className="flex-column gap-10">
          <Text className="L2" color="var(--gray-200)">
            CATEGORIES
          </Text>
          <Container width="164px">
            <CoursesCategoriesSelect
              defaultCategories={defaultCategories}
              onMenuClose={handleAddCategories}
              viewableItems={1}
              controlWidth={'164px'}
            />
          </Container>
        </div>
      </div>
      <Container className="flex ai-center">
        <Text className="H6" color="var(--gray-100)">
          Requirements to pass
        </Text>
        <OptionalComponent display={!!course?.requirements?.length}>
          <Text className="padding-4" color="var(--gray-400)">
            {' '}
            • {course?.requirements?.length}
          </Text>
        </OptionalComponent>
      </Container>
      <Container className="flex-column gap-10 width-100-percent radius-12 padding-20" background="var(--gray-900)">
        {course?.requirements?.map((requirement, i) => (
          <CourseRequirementRow
            key={requirement.uuid}
            requirement={requirement}
            workingEvent={workingEvent}
            setRequirementToDelete={setRequirementToDelete}
            setRequirementToUpdate={setRequirementToUpdate}
            setNewRequirement={setNewRequirement}
            setShowAddNewRequirementModal={setShowAddNewRequirementModal}
            i={i}
          />
        ))}

        <Popover
          open={showAddRequirementPopover}
          onClose={() => setShowAddRequirementPopover(false)}
          ref={popoverRef}
          width="fit-content"
          popoverWidth="100%"
          contentClassName="margin-t-90"
          renderTriggerButton={() => {
            return (
              <OptionalComponent display={canAddMoreRequirements(course?.requirements, workingEvent)}>
                <Button
                  height="64px"
                  width="100%"
                  className="flex ai-center jc-center radius-12"
                  border={`1px dashed ${getThemeColorCSSVariable()}`}
                  onClick={openAddRequirementPopover}
                  data-testid={`course-edit-certificate-design-choose-btn`}
                >
                  <Text color={getThemeColorCSSVariable()} className="flex ai-center gap-8">
                    <Icon name={ICONS.ADD} size={12} />
                    <Text className="L2">Add requirement</Text>
                  </Text>
                </Button>
              </OptionalComponent>
            );
          }}
        >
          <Button
            width="100%"
            hoverBackgroundColor="var(--gray-700)"
            className="radius-0"
            data-testid={`course-edit-requirement-new-watchtime-btn`}
            onClick={() => {
              setNewRequirement({
                ...defaultNewRequirement,
                type: CourseRequirementType.watched_minute,
                settings: defaultSettings(CourseRequirementType.watched_minute),
              });
              setShowAddNewRequirementModal(true);
              setShowAddRequirementPopover(false);
            }}
          >
            <Text className="S3">Watchtime</Text>
          </Button>
          <Button
            width="100%"
            hoverBackgroundColor="var(--gray-700)"
            className="radius-0"
            data-testid={`course-edit-requirement-new-pulse-check-btn`}
            onClick={() => {
              setNewRequirement({
                ...defaultNewRequirement,
                type: CourseRequirementType.pulse_check,
                settings: defaultSettings(CourseRequirementType.pulse_check),
              });
              setShowAddNewRequirementModal(true);
              setShowAddRequirementPopover(false);
            }}
          >
            <Text className="S3">Pulse check</Text>
          </Button>
        </Popover>
      </Container>
    </div>
  );
}
